import {
  KeyboardEvent,
  ReactElement,
  ReactNode,
  cloneElement,
  isValidElement,
  useCallback,
  useEffect,
  useId,
  useRef,
} from 'react';
import cn from 'classnames';
import { CaretDownIcon, MinusIcon, PlusIcon } from '@phosphor-icons/react';

import styles from './SectionAccordion.module.scss';

export type SectionAccordionIcon = 'plus' | 'caret';
export type SectionAccordionSize = 'large' | 'small';

export interface SectionAccordionHeaderProps {
  title: ReactNode;
  subtitle?: ReactNode;
  className?: string;
  titleClassName?: string;
  subtitleClassName?: string;
  size?: SectionAccordionSize;
}

export interface SectionAccordionProps {
  children: [ReactElement, ReactElement];
  icon?: SectionAccordionIcon;
  size?: SectionAccordionSize;
  isOpen?: boolean;
  disabled?: boolean;
  className?: string;
  onStateChange?: (state: 'open' | 'closed') => void;
}

export const Header = ({
  title,
  subtitle,
  className,
  titleClassName,
  subtitleClassName,
  size = 'large',
}: SectionAccordionHeaderProps): ReactElement => (
  <div className={cn(styles.header, className)}>
    <div className={cn(styles.title, titleClassName)}>{title}</div>
    {size === 'large' && subtitle && (
      <div className={cn(styles.subtitle, subtitleClassName)}>{subtitle}</div>
    )}
  </div>
);

export const Collapsible = ({
  children,
}: {
  children: ReactNode;
}): ReactElement => <>{children}</>;

const SectionAccordion = ({
  children,
  icon = 'plus',
  size = 'large',
  isOpen = false,
  disabled = false,
  className,
  onStateChange,
}: SectionAccordionProps): ReactElement => {
  let [header, collapsible] = children;
  if (header.type === Collapsible || collapsible.type === Header) {
    [header, collapsible] = [collapsible, header];
  }

  const isHeaderElement =
    isValidElement<SectionAccordionHeaderProps>(header) &&
    header.type === Header;
  const isCollapsibleElement =
    isValidElement(collapsible) && collapsible.type === Collapsible;

  if (
    process.env.NODE_ENV !== 'production' &&
    (!isHeaderElement || !isCollapsibleElement)
  ) {
    console.error(
      'SectionAccordion requires one SectionAccordion.Header and one SectionAccordion.Collapsible child.'
    );
  }

  const detailsRef = useRef<HTMLDetailsElement>(null);
  const animationRef = useRef<Animation | null>(null);
  const isClosingRef = useRef(false);
  const isExpandingRef = useRef(false);
  const panelId = useId();
  const summaryId = useId();

  useEffect(() => {
    if (detailsRef.current) {
      detailsRef.current.open = disabled ? false : isOpen;
    }
  }, [isOpen, disabled]);

  useEffect(() => {
    const detailsElement = detailsRef.current;
    const summaryElement =
      detailsElement?.querySelector<HTMLElement>('summary');
    const panelElement =
      detailsElement?.querySelector<HTMLDivElement>('summary + div');

    if (
      !detailsElement ||
      !summaryElement ||
      !panelElement ||
      typeof detailsElement.animate !== 'function'
    ) {
      return;
    }

    const prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;

    const clearAnimationState = (
      openState: boolean,
      emitStateChange = false
    ): void => {
      detailsElement.open = openState;
      animationRef.current = null;
      isClosingRef.current = false;
      isExpandingRef.current = false;
      detailsElement.style.height = '';
      detailsElement.style.overflow = '';
      detailsElement.removeAttribute('isclosing');
      if (emitStateChange) {
        onStateChange?.(openState ? 'open' : 'closed');
      }
    };

    const shrink = (): void => {
      isClosingRef.current = true;

      const startHeight = `${detailsElement.offsetHeight}px`;
      const endHeight = `${summaryElement.offsetHeight}px`;

      animationRef.current?.cancel();

      if (prefersReducedMotion) {
        clearAnimationState(false, true);
        return;
      }

      const animation = detailsElement.animate(
        { height: [startHeight, endHeight] },
        {
          duration: 400,
          easing: 'ease-in',
        }
      );
      animationRef.current = animation;
      detailsElement.setAttribute('isclosing', 'true');

      animation.onfinish = () => clearAnimationState(false, true);
      animation.oncancel = () => {
        isClosingRef.current = false;
      };
    };

    const expand = (): void => {
      isExpandingRef.current = true;

      const startHeight = `${detailsElement.offsetHeight}px`;
      const endHeight = `${
        summaryElement.offsetHeight + panelElement.offsetHeight
      }px`;

      animationRef.current?.cancel();

      if (prefersReducedMotion) {
        clearAnimationState(true, true);
        return;
      }

      const animation = detailsElement.animate(
        { height: [startHeight, endHeight] },
        {
          duration: 400,
          easing: 'ease-out',
        }
      );
      animationRef.current = animation;

      animation.onfinish = () => clearAnimationState(true, true);
      animation.oncancel = () => {
        isExpandingRef.current = false;
      };
    };

    const open = (): void => {
      detailsElement.style.height = `${detailsElement.offsetHeight}px`;
      detailsElement.open = true;
      window.requestAnimationFrame(() => expand());
    };

    const onSummaryClick = (event: MouseEvent): void => {
      event.preventDefault();

      if (disabled) {
        return;
      }

      detailsElement.style.overflow = 'hidden';

      if (isClosingRef.current || !detailsElement.open) {
        open();
        return;
      }

      if (isExpandingRef.current || detailsElement.open) {
        shrink();
      }
    };

    summaryElement.addEventListener('click', onSummaryClick);

    return () => {
      summaryElement.removeEventListener('click', onSummaryClick);
      animationRef.current?.cancel();
      clearAnimationState(detailsElement.open);
    };
  }, [onStateChange, disabled]);

  const headerNode = isHeaderElement
    ? cloneElement(header as ReactElement<SectionAccordionHeaderProps>, {
        size,
      })
    : header;

  const onSummaryKeyDown = useCallback(
    (event: KeyboardEvent<HTMLElement>) => {
      const { key, currentTarget } = event;

      if (disabled) {
        if (key === ' ' || key === 'Enter') {
          event.preventDefault();
        }
        return;
      }

      const supportedKeys = ['ArrowDown', 'ArrowUp', 'Home', 'End', 'Escape'];

      if (!supportedKeys.includes(key)) {
        return;
      }

      const detailsElement = currentTarget.closest('details');

      if (key === 'Escape') {
        if (detailsElement?.open) {
          event.preventDefault();
          detailsElement.open = false;
          onStateChange?.('closed');
        }
        return;
      }

      const parentElement = detailsElement?.parentElement;

      if (!parentElement) {
        return;
      }

      const summaryNodes = Array.from(
        parentElement.querySelectorAll<HTMLElement>(
          'details[data-section-accordion] > summary'
        )
      );

      const currentIndex = summaryNodes.indexOf(currentTarget);

      if (summaryNodes.length < 2 || currentIndex === -1) {
        return;
      }

      event.preventDefault();

      if (key === 'Home') {
        summaryNodes[0]?.focus();
        return;
      }

      if (key === 'End') {
        summaryNodes[summaryNodes.length - 1]?.focus();
        return;
      }

      const nextIndex =
        key === 'ArrowDown'
          ? (currentIndex + 1) % summaryNodes.length
          : (currentIndex - 1 + summaryNodes.length) % summaryNodes.length;

      summaryNodes[nextIndex]?.focus();
    },
    [onStateChange, disabled]
  );

  return (
    <details
      className={cn(styles.root, styles[size], className)}
      data-disabled={disabled}
      data-section-accordion
      aria-disabled={disabled}
      ref={detailsRef}
      open={isOpen}
    >
      <summary
        aria-controls={panelId}
        aria-disabled={disabled}
        className={cn(styles.summary, {
          [styles.summaryDisabled]: disabled,
        })}
        id={summaryId}
        onKeyDown={onSummaryKeyDown}
        tabIndex={disabled ? -1 : undefined}
      >
        <div className={styles.summaryInner}>
          {headerNode}
          <span className={styles.iconButton} aria-hidden="true">
            {icon === 'plus' ? (
              <>
                <PlusIcon
                  className={cn(styles.icon, styles.isClosed)}
                  size={16}
                  data-testid="section-accordion-plus-icon"
                  aria-hidden="true"
                />
                <MinusIcon
                  className={cn(styles.icon, styles.isOpen)}
                  size={16}
                  data-testid="section-accordion-minus-icon"
                  aria-hidden="true"
                />
              </>
            ) : (
              <CaretDownIcon
                className={cn(styles.icon, styles.caretIcon)}
                size={24}
                data-testid="section-accordion-caret-icon"
                aria-hidden="true"
              />
            )}
          </span>
        </div>
      </summary>
      <div
        aria-labelledby={summaryId}
        className={styles.panel}
        id={panelId}
        role="region"
      >
        {collapsible}
      </div>
    </details>
  );
};

SectionAccordion.displayName = 'SectionAccordion';
Header.displayName = 'SectionAccordionHeader';
Collapsible.displayName = 'SectionAccordionCollapsible';

export default Object.assign(SectionAccordion, {
  Header,
  Collapsible,
});
